import { Box, Tabs, Stack, Group, Text, Badge, Button, Select, TextInput, Textarea, Modal, Loader, Rating, ActionIcon, Tooltip } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconArrowLeft, IconArrowRight, IconBrandLinkedin, IconBrandWechat, IconBrandWhatsapp, IconDeviceMobile, IconExternalLink, IconMail, IconUserSearch } from '@tabler/icons-react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import useApi from '../hooks/useApi';
import { fetchLead, updateLead, fetchCampaigns, enrichLead, fetchLeads } from '../services/api';
import { VALID_STAGES, STAGE_CONFIG } from '../constants/pipeline';
import LeadTimeline from '../components/LeadTimeline';
import { buildMailtoUrl, buildSmsUrl, buildWhatsappUrl } from '../lib/messageLinks';

function stageColor(stage) {
  return STAGE_CONFIG.find((s) => s.id === stage)?.color || 'gray';
}

// WeChat has no wa.me-style deep link with a prefilled recipient/message, so the
// closest flow is: copy the message, then launch the WeChat app.
async function handleWeChatSend(msg) {
  try {
    await navigator.clipboard.writeText(msg.messageBody || '');
    notifications.show({ title: 'Message copied', message: 'Opening WeChat — pick the contact and paste.', color: 'green' });
  } catch {
    notifications.show({ title: 'Could not copy message', message: 'Copy it manually, then paste in WeChat.', color: 'yellow' });
  }
  window.location.href = 'weixin://';
}

// LinkedIn likewise has no prefilled-compose deep link, so: copy the message,
// then open the person's profile (or a people search when we have no URL).
async function handleLinkedInSend(msg, lead) {
  try {
    await navigator.clipboard.writeText(msg.messageBody || '');
  } catch { /* clipboard unavailable — the user can still copy manually */ }
  const url = lead?.linkedinUrl
    ? lead.linkedinUrl
    : `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(lead?.name || '')}`;
  notifications.show({
    title: 'Message copied',
    message: lead?.linkedinUrl
      ? 'Opening their LinkedIn profile — hit "Message" and paste.'
      : 'Opening a LinkedIn search — open their profile, hit "Message" and paste.',
    color: 'blue',
  });
  window.open(url, '_blank', 'noopener');
}


export default function LeadDetailPage() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignCtx = searchParams.get('campaignId') || null;

  const [lead, setLead] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookedModalOpen, setBookedModalOpen] = useState(false);
  const [bookedAt, setBookedAt] = useState(new Date());
  const [savingBooked, setSavingBooked] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [draftMessages, setDraftMessages] = useState([]);
  const [messagesChanged, setMessagesChanged] = useState(false);
  const [savingMessages, setSavingMessages] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    rating: 0,
    source: '',
    notes: '',
    pitchProfile: '',
  });

  const [nextProspect, setNextProspect] = useState(null);

  useEffect(() => {
    if (!campaignCtx) return;
    fetchLeads(api, { campaignId: campaignCtx, stage: 'prospect', limit: 500 })
      .then((data) => {
        const prospects = (data.leads || []).filter((l) => l.id !== id);
        setNextProspect(prospects[0] || null);
      })
      .catch(() => {});
  }, [api, campaignCtx, id]);

  const campaign = campaigns.find((c) => c.id === lead?.campaignId);
  const canMarkAsBooked = lead?.stage === 'hot' || lead?.stage === 'contacted';
  const canApproveForOutreach = lead?.stage === 'prospect';

  async function refreshLead() {
    const leadData = await fetchLead(api, id);
    setLead(leadData);
    return leadData;
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [leadData, campaignData] = await Promise.all([
          fetchLead(api, id),
          fetchCampaigns(api),
        ]);
        setLead(leadData);
        setDraftMessages(leadData.draftMessages || []);
        setCampaigns(campaignData.campaigns || []);
        setForm({
          name: leadData.name || '',
          email: leadData.email || '',
          phone: leadData.phone || '',
          website: leadData.website || '',
          address: leadData.address || '',
          rating: typeof leadData.rating === 'number' ? leadData.rating : 0,
          source: leadData.source || '',
          notes: leadData.notes || '',
          pitchProfile: leadData.pitchProfile || '',
        });
      } catch (err) {
        notifications.show({
          title: 'Failed to load lead',
          message: err.response?.data?.error || 'Something went wrong',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [api, id]);

  async function persistField(field, value) {
    if (!lead || lead[field] === value) return;
    try {
      const updated = await updateLead(api, id, { [field]: value });
      setLead(updated);
      notifications.show({ title: 'Saved', message: `${field} updated`, color: 'teal', autoClose: 1200 });
    } catch (err) {
      notifications.show({
        title: 'Update failed',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await updateLead(api, id, { archived: true, archivedBy: 'user' });
      notifications.show({ title: 'Archived', message: `${lead.name} has been archived.`, color: 'gray', autoClose: 2000 });
      navigate('/pipeline');
    } catch (err) {
      notifications.show({
        title: 'Archive failed',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setArchiving(false);
      setArchiveModalOpen(false);
    }
  }

  async function handleStageChange() {
    if (!lead) return;
    const index = VALID_STAGES.indexOf(lead.stage);
    const nextStage = VALID_STAGES[(index + 1) % VALID_STAGES.length];
    try {
      const updated = await updateLead(api, id, { stage: nextStage });
      setLead(updated);
      notifications.show({ title: 'Stage updated', message: nextStage, color: 'teal', autoClose: 1200 });
    } catch (err) {
      notifications.show({
        title: 'Stage update failed',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  async function handleConfirmBooked() {
    if (!lead) return;
    const chosen = bookedAt instanceof Date ? bookedAt : new Date();

    setSavingBooked(true);
    try {
      await updateLead(api, id, { stage: 'booked', bookedAt: chosen.toISOString() });
      await refreshLead();
      setBookedModalOpen(false);
      notifications.show({ title: 'Success', message: 'Lead marked as Booked', color: 'teal' });
    } catch (err) {
      notifications.show({
        title: 'Could not mark as Booked',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setSavingBooked(false);
    }
  }

  async function handleEnrich() {
    setEnriching(true);
    try {
      const result = await enrichLead(api, id);
      if (result.updated) {
        setLead(result.lead);
        setForm((f) => ({
          ...f,
          email: result.lead.email || f.email,
          phone: result.lead.phone || f.phone,
        }));
        const found = result.fields.join(' and ');
        notifications.show({ title: 'Contact info found', message: `Apollo returned ${found} for this lead.`, color: 'teal' });
      } else {
        notifications.show({ title: 'No match found', message: 'Apollo could not find contact info for this lead.', color: 'gray' });
      }
    } catch (err) {
      notifications.show({ title: 'Enrichment failed', message: err.response?.data?.error || 'Something went wrong', color: 'red' });
    } finally {
      setEnriching(false);
    }
  }

  async function handleApproveForOutreach() {
    try {
      const updated = await updateLead(api, id, { stage: 'qualified' });
      setLead(updated);
      notifications.show({ title: 'Approved', message: 'Lead approved for outreach', color: 'violet', autoClose: 2000 });
    } catch (err) {
      notifications.show({
        title: 'Could not approve',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }

  async function saveDraftMessages() {
    setSavingMessages(true);
    try {
      const updated = await updateLead(api, id, { draftMessages });
      setLead(updated);
      setDraftMessages(updated.draftMessages || draftMessages);
      setMessagesChanged(false);
      notifications.show({ title: 'Messages saved', message: 'Outreach messages updated', color: 'teal', autoClose: 1500 });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setSavingMessages(false);
    }
  }

  if (loading || !lead) {
    return (
      <Box p="md">
        <Loader color="aviary" />
      </Box>
    );
  }

  return (
    <Box p="md">
      <Group mb="md" justify="space-between">
        <Button
          variant="subtle"
          component={Link}
          to={campaignCtx ? `/pipeline?campaignId=${campaignCtx}` : '/pipeline'}
          leftSection={<IconArrowLeft size={14} />}
        >
          Pipeline
        </Button>
        {campaignCtx && (
          <Button
            variant="subtle"
            component={Link}
            to={nextProspect ? `/leads/${nextProspect.id}?campaignId=${campaignCtx}` : '#'}
            rightSection={<IconArrowRight size={14} />}
            disabled={!nextProspect}
          >
            {nextProspect ? `Next: ${nextProspect.name}` : 'No more prospects'}
          </Button>
        )}
      </Group>

      <Group mb="md" justify="space-between" align="flex-start">
        <Box>
          <Group gap="sm" align="center">
            <Text fw={700} size="xl">{lead.name}</Text>
            <Rating
              value={form.rating}
              fractions={2}
              size="md"
              color="yellow"
              onChange={(value) => {
                setForm((f) => ({ ...f, rating: value }));
                persistField('rating', value);
              }}
            />
          </Group>
          <Group gap="xs" mt={2}>
            {lead.company && <Text c="dimmed" size="sm">{lead.company}</Text>}
            {campaign && <Badge variant="outline" color="teal" size="xs">{campaign.name}</Badge>}
          </Group>
        </Box>
        <Group gap="xs">
          {canApproveForOutreach && (
            <Button color="violet" variant="filled" onClick={handleApproveForOutreach}>
              Approve for Outreach
            </Button>
          )}
          <Badge color={stageColor(lead.stage)} size="lg" style={{ cursor: 'pointer' }} onClick={handleStageChange}>
            {lead.stage}
          </Badge>
          {canMarkAsBooked && (
            <Button
              color="teal"
              variant="filled"
              onClick={() => {
                setBookedAt(new Date());
                setBookedModalOpen(true);
              }}
            >
              Mark as Booked
            </Button>
          )}
          {lead.score > 0 && <Badge variant="light" color="aviary">{lead.score}</Badge>}
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => setArchiveModalOpen(true)}
          >
            Archive
          </Button>
        </Group>
      </Group>

      <Modal opened={bookedModalOpen} onClose={() => setBookedModalOpen(false)} title="Mark as Booked" centered>
        <Stack>
          <DateTimePicker
            label="When is the call?"
            value={bookedAt}
            onChange={(value) => setBookedAt(value || new Date())}
            clearable={false}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setBookedModalOpen(false)} disabled={savingBooked}>
              Cancel
            </Button>
            <Button color="teal" onClick={handleConfirmBooked} loading={savingBooked}>
              Confirm Booked
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={archiveModalOpen} onClose={() => setArchiveModalOpen(false)} title="Archive lead?" centered size="sm">
        <Stack>
          <Text size="sm">
            <strong>{lead.name}</strong> will be hidden from the pipeline. You can find archived leads via the API if needed.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setArchiveModalOpen(false)} disabled={archiving}>Cancel</Button>
            <Button color="gray" onClick={handleArchive} loading={archiving}>Archive</Button>
          </Group>
        </Stack>
      </Modal>

      <Tabs defaultValue="info">
        <Tabs.List mb="md">
          <Tabs.Tab value="info">Info</Tabs.Tab>
          <Tabs.Tab value="outreach">Outreach Messages</Tabs.Tab>
          <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="info">
          <Stack gap="sm" maw={720}>
            <TextInput
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))}
              onBlur={() => persistField('name', form.name)}
            />
            <TextInput
              label="Email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.currentTarget.value }))}
              onBlur={() => persistField('email', form.email)}
            />
            <TextInput
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.currentTarget.value }))}
              onBlur={() => persistField('phone', form.phone)}
            />
            <Group justify="flex-end">
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconUserSearch size={14} />}
                loading={enriching}
                onClick={handleEnrich}
              >
                Find contact info via Apollo
              </Button>
            </Group>
            <TextInput
              label="Website"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.currentTarget.value }))}
              onBlur={() => persistField('website', form.website)}
              rightSection={
                form.website ? (
                  <Tooltip label="Open website" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      component="a"
                      href={form.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconExternalLink size={16} />
                    </ActionIcon>
                  </Tooltip>
                ) : null
              }
            />
            <TextInput
              label="Address"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.currentTarget.value }))}
              onBlur={() => persistField('address', form.address)}
            />
            <Select
              label="Source"
              data={['instagram-dm', 'linkedin', 'referral', 'cold-email', 'whatsapp', 'sms', 'other']}
              value={form.source}
              onChange={(value) => {
                const next = value || '';
                setForm((f) => ({ ...f, source: next }));
                persistField('source', next);
              }}
            />
            <Textarea
              label="Notes"
              minRows={3}
              autosize
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.currentTarget.value }))}
              onBlur={() => persistField('notes', form.notes)}
            />
            <Textarea
              label="Pitch Profile"
              description="Research profile used by the bot to personalise messages. Edit before the first touch is sent."
              placeholder={`Owner: \nBusiness context: \nPain point: \nWhy us: \nSuggested hook: `}
              minRows={5}
              autosize
              value={form.pitchProfile}
              onChange={(e) => setForm((f) => ({ ...f, pitchProfile: e.currentTarget.value }))}
              onBlur={() => persistField('pitchProfile', form.pitchProfile)}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="outreach">
          <Stack gap="md" maw={720} mt="sm">
            {draftMessages.length === 0 ? (
              <Text size="sm" c="dimmed">No outreach messages yet. Add this lead to a campaign to generate messages.</Text>
            ) : (
              <>
                {draftMessages.map((msg, idx) => (
                  <Box key={msg.touchId || idx} p="md" style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8 }}>
                    <Group mb="xs" justify="space-between">
                      <Group gap="xs">
                        <Text size="sm" fw={600}>Touch {idx + 1}</Text>
                        <Badge size="xs" variant="light" color="blue">{msg.channel}</Badge>
                        {typeof msg.sendDayOffset === 'number' && (
                          <Text size="xs" c="dimmed">Day {msg.sendDayOffset}</Text>
                        )}
                      </Group>
                      <Group gap="xs">
                        <Tooltip label={lead.phone ? 'Open in WhatsApp' : 'No phone number'} position="left">
                          <ActionIcon
                            component="a"
                            href={lead.phone ? buildWhatsappUrl(lead, msg.messageBody || '') : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="subtle"
                            color="green"
                            size="sm"
                            disabled={!lead.phone}
                          >
                            <IconBrandWhatsapp size={15} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={lead.phone ? 'Send SMS' : 'No phone number'} position="left">
                          <ActionIcon
                            component="a"
                            href={lead.phone ? buildSmsUrl(lead, msg.messageBody || '') : undefined}
                            variant="subtle"
                            color="blue"
                            size="sm"
                            disabled={!lead.phone}
                          >
                            <IconDeviceMobile size={15} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={lead.email ? 'Open in email app' : 'No email address'} position="left">
                          <ActionIcon
                            component="a"
                            href={lead.email ? buildMailtoUrl(lead, msg.messageBody || '') : undefined}
                            variant="subtle"
                            color="gray"
                            size="sm"
                            disabled={!lead.email}
                          >
                            <IconMail size={15} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Copy message & open WeChat" position="left">
                          <ActionIcon
                            variant="subtle"
                            color="teal"
                            size="sm"
                            onClick={() => handleWeChatSend(msg)}
                          >
                            <IconBrandWechat size={15} />
                          </ActionIcon>
                        </Tooltip>
                        {(lead.linkedinUrl || lead.source === 'apollo-search' || lead.source === 'linkedin') && (
                          <Tooltip
                            label={lead.linkedinUrl ? 'Copy message & open LinkedIn profile' : 'Copy message & search on LinkedIn'}
                            position="left"
                          >
                            <ActionIcon
                              variant="subtle"
                              color="indigo"
                              size="sm"
                              onClick={() => handleLinkedInSend(msg, lead)}
                            >
                              <IconBrandLinkedin size={15} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Group>
                    <Textarea
                      minRows={4}
                      autosize
                      value={msg.messageBody || ''}
                      onChange={(e) => {
                        const next = [...draftMessages];
                        next[idx] = { ...next[idx], messageBody: e.currentTarget.value };
                        setDraftMessages(next);
                        setMessagesChanged(true);
                      }}
                    />
                    {msg.linkUrl && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Link: {msg.linkUrl}
                        {msg.destinationUrl && (
                          <>
                            {' → '}
                            <Text span c="dimmed">{msg.destinationUrl}</Text>
                          </>
                        )}
                      </Text>
                    )}
                  </Box>
                ))}
                <Group justify="flex-end">
                  <Button
                    color="teal"
                    variant="filled"
                    disabled={!messagesChanged}
                    loading={savingMessages}
                    onClick={saveDraftMessages}
                  >
                    Save Messages
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="timeline">
          <LeadTimeline leadId={id} />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
